/**
 * ================================================================
 * FirstCut - Generation Progress Component
 * ================================================================
 * Shows detailed progress during content generation with:
 * - Progress stages with animations
 * - Retry feedback when AI is busy
 * - Estimated time remaining
 * - Friendly messages during long waits
 * ================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FiLoader, FiCheck, FiRefreshCw, FiClock, FiAlertTriangle } from 'react-icons/fi';

const GENERATION_STAGES = [
  { id: 'preparing', label: 'Preparing your content...', duration: 3000 },
  { id: 'analyzing', label: 'Analyzing brand context...', duration: 5000 },
  { id: 'generating', label: 'AI is creating your content...', duration: 15000 },
  { id: 'polishing', label: 'Adding finishing touches...', duration: 8000 },
  { id: 'complete', label: 'Almost there!', duration: 0 }
];

const WAIT_MESSAGES = [
  { time: 30000, message: "AI is working on something special..." },
  { time: 60000, message: "This one is taking a bit longer, but it'll be worth it!" },
  { time: 90000, message: "High demand right now. Thanks for your patience!" },
  { time: 120000, message: "Still working... Complex content takes a bit more time." },
  { time: 180000, message: "Almost there! The AI is being extra thorough." }
];

const RETRY_MESSAGES = [
  "AI is busy, retrying...",
  "Finding available AI resources...",
  "Still working on it...",
  "Almost there, just a moment more..."
];

function GenerationProgress({ 
  isGenerating = false, 
  retryCount = 0, 
  currentStage = null,
  error = null,
  onRetry = null 
}) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stageIndex, setStageIndex] = useState(0);
  const [waitMessage, setWaitMessage] = useState(null);

  // Reset when generation starts
  useEffect(() => {
    if (isGenerating) {
      setElapsedTime(0);
      setStageIndex(0);
      setWaitMessage(null);
    }
  }, [isGenerating]);

  // Track elapsed time
  useEffect(() => {
    if (!isGenerating) return;

    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1000);
    }, 1000);

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Update stages based on time
  useEffect(() => {
    if (!isGenerating) return;

    let totalDuration = 0;
    for (let i = 0; i < GENERATION_STAGES.length; i++) {
      totalDuration += GENERATION_STAGES[i].duration;
      if (elapsedTime < totalDuration) {
        setStageIndex(i);
        break;
      }
    }
  }, [elapsedTime, isGenerating]);

  // Update wait messages for long generations
  useEffect(() => {
    if (!isGenerating) return;

    for (let i = WAIT_MESSAGES.length - 1; i >= 0; i--) {
      if (elapsedTime >= WAIT_MESSAGES[i].time) {
        setWaitMessage(WAIT_MESSAGES[i].message);
        break;
      }
    }
  }, [elapsedTime, isGenerating]);

  // Format elapsed time
  const formatTime = useCallback((ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }, []);

  if (!isGenerating && !error) return null;

  const currentStageData = GENERATION_STAGES[stageIndex];
  const isRetrying = retryCount > 0;

  // Error state with retry option
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <FiAlertTriangle className="text-red-500" size={20} />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-red-400 mb-1">Generation Issue</h4>
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-colors"
                data-testid="retry-generation-btn"
              >
                <FiRefreshCw size={16} />
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-secondary/50 border border-border/50 rounded-xl p-4" data-testid="generation-progress">
      {/* Main progress indicator */}
      <div className="flex items-center gap-4 mb-4">
        <div className={`
          w-12 h-12 rounded-full flex items-center justify-center
          ${isRetrying ? 'bg-amber-500/20' : 'bg-primary/20'}
        `}>
          {isRetrying ? (
            <FiRefreshCw className="text-amber-500 animate-spin" size={24} />
          ) : (
            <FiLoader className="text-primary animate-spin" size={24} />
          )}
        </div>
        <div className="flex-1">
          <h4 className={`font-medium ${isRetrying ? 'text-amber-400' : 'text-foreground'}`}>
            {isRetrying 
              ? RETRY_MESSAGES[Math.min(retryCount - 1, RETRY_MESSAGES.length - 1)]
              : currentStageData?.label || 'Processing...'}
          </h4>
          {waitMessage && (
            <p className="text-sm text-muted-foreground mt-0.5">{waitMessage}</p>
          )}
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1 text-muted-foreground">
            <FiClock size={14} />
            <span className="text-sm font-mono">{formatTime(elapsedTime)}</span>
          </div>
          {retryCount > 0 && (
            <p className="text-xs text-amber-500/80 mt-0.5">Retry #{retryCount}</p>
          )}
        </div>
      </div>

      {/* Progress stages */}
      <div className="flex gap-1">
        {GENERATION_STAGES.slice(0, -1).map((stage, idx) => (
          <div 
            key={stage.id}
            className={`
              flex-1 h-1.5 rounded-full transition-all duration-500
              ${idx < stageIndex ? 'bg-primary' : ''}
              ${idx === stageIndex ? 'bg-primary/50 animate-pulse' : ''}
              ${idx > stageIndex ? 'bg-border' : ''}
            `}
          />
        ))}
      </div>

      {/* Stage labels on hover/mobile */}
      <div className="flex justify-between mt-2">
        {GENERATION_STAGES.slice(0, -1).map((stage, idx) => (
          <div 
            key={stage.id}
            className={`
              text-xs transition-colors
              ${idx <= stageIndex ? 'text-muted-foreground' : 'text-muted-foreground/50'}
            `}
          >
            {idx === stageIndex && <span className="text-primary">●</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default GenerationProgress;
